import * as React from "react";
import clsx from "clsx";
import {
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Check,
  Info,
  Lightbulb,
  Minus,
  Plus,
  type LucideIcon,
} from "lucide-react";
import {
  ALERT_SEVERITY_LEVELS,
  type AlertPrefsDraft,
} from "./productAlertSubscription";
import * as s from "./ProductSubscriptionsModal.css";
import { useI18n } from "../../lib/i18n";

const SEVERITY_ICONS: Record<(typeof ALERT_SEVERITY_LEVELS)[number]["icon"], LucideIcon> = {
  lightbulb: Lightbulb,
  info: Info,
  circleAlert: AlertCircle,
  triangleAlert: AlertTriangle,
  octagonAlert: AlertOctagon,
};

export type AlertSeverityTogglesProps = {
  prefs: AlertPrefsDraft;
  onChange: (next: AlertPrefsDraft) => void;
  /** When set, toggles that differ from baseline get channel-style unsaved chrome. */
  baselinePrefs?: AlertPrefsDraft | null;
  /** Show severity name next to the icon (configure modal). */
  showLabels?: boolean;
  /** Center the chip group (configure modal). */
  centered?: boolean;
  disabled?: boolean;
};

export function AlertSeverityToggles({
  prefs,
  onChange,
  baselinePrefs = null,
  showLabels = false,
  centered = false,
  disabled,
}: AlertSeverityTogglesProps) {
  const { t } = useI18n();
  const toggleSeverity = (level: number) => {
    if (disabled) return;
    const set = new Set(prefs.enabledSeverities);
    if (set.has(level)) set.delete(level);
    else set.add(level);
    onChange({
      ...prefs,
      enabledSeverities: [...set].sort((a, b) => a - b),
    });
  };

  return (
    <div
      className={clsx(
        s.alertSeverityChips,
        centered && s.alertSeverityChipsCentered,
      )}
      role="group"
      aria-label={t("pro.products.alerts.severitiesAria")}
    >      {ALERT_SEVERITY_LEVELS.map((opt) => {
        const active = prefs.enabledSeverities.includes(opt.level);
        const baselineActive = Boolean(
          baselinePrefs?.enabledSeverities.includes(opt.level),
        );
        const dirty = Boolean(baselinePrefs) && active !== baselineActive;
        const Icon = SEVERITY_ICONS[opt.icon];
        const title = dirty ? `${opt.label} (unsaved)` : opt.label;
        return (
          <button
            key={opt.level}
            type="button"
            className={clsx(
              s.severityChip,
              showLabels && s.severityChipLabeled,
              dirty && s.dirtyControl,
            )}
            data-active={active ? "true" : "false"}
            data-unsaved={dirty ? "true" : undefined}
            data-severity={String(opt.level)}
            disabled={disabled}
            aria-pressed={active}
            aria-label={title}
            title={title}
            onClick={() => toggleSeverity(opt.level)}
          >
            <Icon size={14} strokeWidth={2.25} aria-hidden />
            {showLabels ? <span>{opt.label}</span> : null}
            {active && dirty ? (
              <span className={s.channelToggleUnsavedBadge} aria-hidden>
                <Plus size={9} strokeWidth={3} />
              </span>
            ) : active ? (
              <span className={s.channelToggleCheck} aria-hidden>
                <Check size={9} strokeWidth={3} />
              </span>
            ) : dirty ? (
              <span className={s.channelToggleUnsavedBadge} aria-hidden>
                <Minus size={9} strokeWidth={3} />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
